import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@core/components/ui';
import { logger } from '@core/utils';

/**
 * Text file viewer with monospace rendering and scroll.
 */
export function TextViewer({ url }) {
  const [content, setContent] = useState(null);
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!url) return;

    let cancelled = false;

    async function fetchContent() {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(url);
        if (!response.ok) {
          throw Object.assign(new Error(`Failed to fetch file: ${response.status}`), { status: response.status });
        }
        const text = await response.text();
        if (!cancelled) {
          setContent(text);
        }
      } catch (err) {
        logger.error('TextViewer fetch error:', err);
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchContent();
    return () => { cancelled = true; };
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-[var(--color-danger)]">
        <p className="text-sm">
          {error.status ? t('dataroom.viewer.fetchFailed', { status: error.status }) : error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-[var(--color-bg-subtle)] p-4">
      <pre className="text-sm font-mono whitespace-pre-wrap break-words text-[var(--color-text)]">
        <code>{content}</code>
      </pre>
    </div>
  );
}

TextViewer.propTypes = {
  url: PropTypes.string.isRequired,
};
